/**
 * User Preferences Service
 *
 * This service manages user preferences with both browser storage and back-end API support.
 * If the API is available, its values take precedence. If not, browser storage is used as a fallback.
 */

import safeLocalStorage from '@grantstreet/psc-js/utils/safe-local-storage.js'
import VueCookies from 'vue-cookies'
import { reactive } from 'vue'
import PreferencesApiClient from './api-client.js'
import { sentryException } from './sentry.js'

// Known preference keys
export const PREFERENCE_KEYS = {
  LOCALE: 'payhubDefaultLocale',
  NOTIFICATIONS_SEEN: 'notifications-have-been-seen-pre-expanded',
  INTRO_FEATURES_SEEN: 'payhub-intro-last-features-seen',
}

// Storage types
const STORAGE_TYPES = {
  LOCAL: 'localStorage',
  SESSION: 'sessionStorage',
}

// Map of preference keys to their storage types
const PREFERENCE_STORAGE_MAP = {
  [PREFERENCE_KEYS.LOCALE]: STORAGE_TYPES.LOCAL,
  [PREFERENCE_KEYS.NOTIFICATIONS_SEEN]: STORAGE_TYPES.SESSION,
  [PREFERENCE_KEYS.INTRO_FEATURES_SEEN]: STORAGE_TYPES.SESSION,
}

// Export preferences as a reactive property
export const preferences = reactive({})

class PreferencesService {
  constructor (options = {}) {
    this.apiClient = options.apiClient
    this.initPromise = new Promise((resolve, reject) => {
      this.initCache().then(() => {
        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  }

  /**
   * Get browser storage for a specific preference key
   * @param {string} key - The preference key
   * @returns {Storage} - The appropriate storage object (localStorage or sessionStorage)
   */
  _getStorageForKey (key) {
    const storageType = PREFERENCE_STORAGE_MAP[key]

    if (storageType === STORAGE_TYPES.SESSION) {
      return window.sessionStorage
    }

    return safeLocalStorage
  }

  /**
   * Get preference from browser storage
   * @param {string} key - The preference key
   * @returns {string|null} - The preference value or null if not found
   */
  getFromBrowserStorage (key) {
    const storage = this._getStorageForKey(key)
    if (!storage) return null

    try {
      return storage.getItem(key)
    }
    catch (error) {
      console.error(`Error getting preference '${key}' from browser storage:`, error)
      return null
    }
  }

  /**
   * Set preference in browser storage
   * @param {string} key - The preference key
   * @param {string} value - The preference value
   */
  setToBrowserStorage (key, value) {
    const storage = this._getStorageForKey(key)
    if (!storage) return

    try {
      storage.setItem(key, value)
    }
    catch (error) {
      console.error(`Error setting preference '${key}' in browser storage:`, error)
    }
  }

  /**
   * Get preferences from API
   * @returns {Promise<Object>} - Promise resolving to preferences object or null
   */
  async getFromApi () {
    try {
      const apiClient = this._getApiClient()
      const result = await apiClient.getPreferences()
      return result?.data
    }
    catch (error) {
      // Don't log 404s - just means no preferences saved yet
      if (error.response?.status !== 404) {
        sentryException('Error fetching preferences from API:', error)
      }

      return null
    }
  }

  /**
   * Set preference to API with timeout
   * @param {string} key - The preference key
   * @param {string} value - The preference value
   * @returns {Promise<boolean>} - Promise resolving to true if successful
   */
  async setToApi (key, value) {
    try {
      const apiClient = this._getApiClient()
      const result = await apiClient.setPreferences({ [key]: value })
      return result.status === 200
    }
    catch (error) {
      console.error(error)
      sentryException(`Error saving preference '${key}' to API:`, error)
      return false
    }
  }

  /**
   * Initialize the cache with all known preferences
   * This should be called once at application startup
   * @returns {Promise<Object>} - An object containing all preferences
   */
  async initCache () {
    // Get values from browser storage first (faster)
    let prefs = this._getAllFromBrowserStorage()

    try {
      // Then get values from API (takes precedence)
      const apiPrefs = await this.getFromApi()
      prefs = { ...prefs, ...apiPrefs }
    }
    catch (error) {
      console.warn('Error loading preferences from API, using browser storage only:', error)
    }

    // Copy all preferences to the reactive state
    for (const key in prefs) {
      preferences[key] = prefs[key]
    }
  }

  clearCache () {
    // Clear all keys from preferences object
    for (const k of Object.keys(preferences)) {
      delete preferences[k]
    }
  }

  /**
   * Set a preference in browser storage, then update cache and API
   * @param {string} key - The preference key
   * @param {any} value - The preference value
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async setPreference (key, value) {
    preferences[key] = value
    this.setToBrowserStorage(key, value)

    return this.setToApi(key, value)
      .catch(sentryException)
  }

  /**
   * Helper method to get all preferences from browser storage
   * @private
   * @returns {Object} - Object containing all preferences from browser storage
   */
  _getAllFromBrowserStorage () {
    const ret = {}
    Object.values(PREFERENCE_KEYS).forEach(key => {
      const value = this.getFromBrowserStorage(key)
      if (value !== null) {
        ret[key] = value
      }
    })
    return ret
  }

  /**
   * Get the govhub_session cookie value
   * @returns {string|null} - The govhub_session cookie value or null
   * @private
   */
  _getGovhubSessionId () {
    return VueCookies.get('govhub_session')
  }

  /**
   * Get or create the API client instance
   * @returns {PreferencesApiClient} - The API client instance
   * @private
   */
  _getApiClient () {
    if (!this.apiClient) {
      // Get the actual govhub_session cookie value
      const sessionId = this._getGovhubSessionId()
      if (!sessionId) {
        throw new Error("No govhub_session cookie found - can't initialize user-preferences")
      }

      this.apiClient = new PreferencesApiClient({
        sessionId,
        // Add Sentry exception logger for API client errors
        exceptionLogger: sentryException,
      })
    }
    return this.apiClient
  }
}

// Export a singleton instance
const singleton = new PreferencesService()
export default singleton
